import { useMemo, useEffect } from 'react'
import merge from 'lodash/merge'
import type { FilterAndSortOptions } from 'src/state/redux/selector/common'
import type { DocumentORM } from 'src/types/orms'
import createContextProxy from 'src/state/context/contextProxy'
import type { SearchContextValue, Mode } from 'src/hooks/useDocumentSearchV2'
import { useAllDocumentsInstance } from 'src/state/redux/selector/document'

const documentSearchProvider = createContextProxy<SearchContextValue>('DocumentSearch')

export const DocumentSearchProxyContext = documentSearchProvider.ContextProxy
export const DocumentSearchProxyProvider = documentSearchProvider.ContextProxyProvider
export const useDocumentSearchV2ProxyContext = documentSearchProvider.useProxy

// Create a version of useDocumentSearchV2 that uses the proxy context
export const useDocumentSearchV2Proxy = (
  searchText?: string,
  mode?: Mode,
  ...queriesIn: FilterAndSortOptions<DocumentORM>[]
) => {
  const context = useDocumentSearchV2ProxyContext();

  useEffect(
    () => {
      if (searchText) {
        context.fullTextSearch(searchText, mode);
      } else {
        context.resetSearch();
      }
    },
    [searchText, mode],
  )

  const searchQuery: FilterAndSortOptions<DocumentORM> | undefined = useMemo(
    () => merge(context.query, ...queriesIn),
    [context.query, ...queriesIn],
  )

  const docORMResults = useAllDocumentsInstance(searchQuery)

  const value = useMemo(
    () => ({
      ...context,
      searchQuery,
      documentORMSearchResults: docORMResults,
    }),
    [context, searchQuery, docORMResults],
  )

  return value
}
